import { useEffect, useRef } from 'react';
import axios, { InternalAxiosRequestConfig } from 'axios';
// import { getNavigateFunction } from './useNavigateHook';
import { decodeJwt } from 'jose';

// Définition de l'interface pour le token décodé
interface DecodedToken {
  exp: number;
  [key: string]: any;
}

// Définition de l'interface pour la réponse de l'API
interface TokenResponse {
  access_token: string;
  refresh_token: string;
  id_token: string;
  expires_in: number;
}

interface AxiosInterceptorProps {
  logout: () => void; // Fonction de déconnexion passée en props
}

const AxiosInterceptor: React.FC<AxiosInterceptorProps> = ({ logout }) => {
  // Utiliser une référence pour suivre si un rafraîchissement est en cours
  const isRefreshing = useRef(false);
  // File d'attente pour les requêtes en attente pendant le rafraîchissement
  const requestQueue = useRef<Array<{
    resolve: (value?: any) => void;
    reject: (reason?: any) => void;
  }>>([]);


 
  const refreshAccessToken = async (): Promise<string | null> => {
    const refreshToken = localStorage.getItem('refresh_token');
    console.log('Refresh token tentative');
    
    if (!refreshToken) {
      console.error('Refresh token manquant');
      return null;
    }

    // Vérification de l'expiration du refresh token
    // if (isRefreshTokenExpired()) {
    //   console.error('Refresh token expiré, déconnexion en cours...');
    //   logout();
    //   return null;
    // }

    try {
      const tokenEndpoint = import.meta.env.VITE_REDIRECT_Otenti;
      
      // Utilisez un nouvel instance d'axios pour éviter l'intercepteur
      const axiosInstance = axios.create();
      
      const response = await axiosInstance.post<TokenResponse>(
        `${tokenEndpoint}/connect/token`,
        new URLSearchParams({
          grant_type: 'refresh_token',
          refresh_token: refreshToken,
          client_id: 'AdminOtentiCLient',
          client_secret: 'Otenti-Secret',
        }),
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        }
      );
      
      localStorage.setItem('access_token', response.data.access_token);
      if (response.data.refresh_token) {
        localStorage.setItem('refresh_token', response.data.refresh_token);
      }
      
      return response.data.access_token;
    } catch (error) {
      console.error('Erreur lors du rafraîchissement du token:', error);
      // Si l'erreur est due à un refresh token invalide/expiré, déconnexion
      logout();
      return null;
    }
  };

  useEffect(() => {
   
    // Intercepteur pour les requêtes
    const requestInterceptor = axios.interceptors.request.use(
      async (config: InternalAxiosRequestConfig) => {
        config.withCredentials = true;
        // Ne pas ajouter d'en-tête d'autorisation pour la requête de rafraîchissement du token
        if (config.url?.includes('/connect/token')) {
          return config;
        }

        const accessToken = localStorage.getItem('access_token');
        if (!accessToken) {
          return config;
        }

        try {
          const decodedToken = decodeJwt(accessToken) as DecodedToken;
          const isExpired = decodedToken.exp * 1000 < Date.now();

          if (isExpired) {
            // Vérifier si le refresh token est expiré
            // if (isRefreshTokenExpired()) {
            //   console.log('Refresh token expiré, déconnexion');
            //   logout();
            //   return Promise.reject('Refresh token expiré');
            // }
            
            if (!isRefreshing.current) {
              isRefreshing.current = true;
              
              try {
                const newAccessToken = await refreshAccessToken();
                isRefreshing.current = false;
                
                if (newAccessToken) {
                  // Exécuter toutes les requêtes en attente avec le nouveau token
                  requestQueue.current.forEach(({resolve}) => {
                    resolve();
                  });
                  requestQueue.current = [];
                  
                  config.headers.Authorization = `Bearer ${newAccessToken}`;
                } else {
                  // Si le rafraîchissement échoue, rediriger vers la page de connexion
                  requestQueue.current.forEach(({reject}) => {
                    reject('Échec du rafraîchissement de token');
                  });
                  requestQueue.current = [];
                  
                  logout();
                  return Promise.reject('Échec du rafraîchissement de token');
                }
              } catch (error) {
                isRefreshing.current = false;
                logout();
                return Promise.reject(error);
              }
            } else {
              // Si un rafraîchissement est déjà en cours, mettre la requête en file d'attente
              return new Promise((resolve, reject) => {
                requestQueue.current.push({resolve, reject});
              }).then(() => {
                config.headers.Authorization = `Bearer ${localStorage.getItem('access_token')}`;
                return config;
              }).catch((error) => {
                return Promise.reject(error);
              });
            }
          } else {
            config.headers.Authorization = `Bearer ${accessToken}`;
          }
        } catch (error) {
          console.error('Erreur lors du décodage du token:', error);
        }
        
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    
    );
    const responseInterceptor = axios.interceptors.response.use(
      response => response,
      async error => {
        if(error.response?.status === 401 && error.response.headers['x-token-expired'] === 'true')
        {
            await new Promise(resolve => setTimeout(resolve, 100))
          console.log("retry request....")
            return axios(error.configuration)
        }

        return Promise.reject(error);
      }
    );
    // Nettoyage des intercepteurs lors du démontage du composant
    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
      // axios.interceptors.reponse.eject(responseInterceptor);
    };
  }, [logout]);

  return null; // Ce composant ne rend rien visuellement
};

export default AxiosInterceptor;
